import { Users } from 'lucide-react'
import { SectionCard } from '@/components/ui/SectionCard'
import tabStyles from '@/features/company/styles/companyInfoTab.module.css'
import type { PropertyContactsTabProps } from '../types'
import { usePropertyContactsTab } from '../hooks/usePropertyContactsTab'
import { PropertyContactsToolbar } from './PropertyContactsToolbar'
import { PropertyContactsStaffTable } from './PropertyContactsStaffTable'
import { PropertyContactsDialogs } from './PropertyContactsDialogs'

export function PropertyContactsTab(props: PropertyContactsTabProps) {
  const tab = usePropertyContactsTab(props)

  const {
    property,
    propertyId,
    companyId,
    canEdit,
    canLogCall,
    canImpersonate,
    canRemoveRole,
    canShowStaffActions,
    isLibertyRole,
    propertyStaff,
    staffLoading,
    showSelectBox,
    setShowSelectBox,
    selectedUserIds,
    selectableUserIdsForEmail,
    allEmailRowsSelected,
    someEmailRowsSelected,
    toggleSelectAllEmail,
    toggleUserSelection,
    cancelEmail,
    handleComposeClick,
    handleUserManagement,
    handleEditUser,
    handleImpersonate,
    handleRemoveRoleClick,
    transferOpen,
    setTransferOpen,
    addUserOpen,
    setAddUserOpen,
    logCallStaff,
    setLogCallStaff,
    loadStaff,
    onTransferSuccess,
    composeConfirmOpen,
    setComposeConfirmOpen,
    onComposeConfirmed,
    emailBroadcastOpen,
    setEmailBroadcastOpen,
    onEmailBroadcastDone,
    removeConfirm,
    setRemoveConfirm,
    executeRemoveRole,
    deleteUserConfirm,
    handleDeleteUserChoice,
    deleteOrDisableConfirm,
    setDeleteOrDisableConfirm,
    executeDeleteOrDisable,
    welcomeConfirm,
    setWelcomeConfirm,
    executeWelcomeEmail,
  } = tab

  return (
    <div
      data-testid="property-contacts-tab"
      className={`${tabStyles.tabContent} animate-in fade-in duration-300`}
    >
      {canEdit && (
        <PropertyContactsToolbar
          showSelectBox={showSelectBox}
          composeDisabled={selectedUserIds.length === 0}
          onTransfer={() => setTransferOpen(true)}
          onToggleEmailMode={() => setShowSelectBox((b) => !b)}
          onUserManagement={handleUserManagement}
          onAddUser={() => setAddUserOpen(true)}
          onCancelEmail={cancelEmail}
          onCompose={handleComposeClick}
        />
      )}

      <SectionCard
        icon={Users}
        title="Contact Information"
        testId="property-contacts-section-contact-information"
        titleTestId="property-contacts-section-contact-information-title"
      >
        <PropertyContactsStaffTable
          staffLoading={staffLoading}
          propertyStaff={propertyStaff}
          propertyId={propertyId}
          showSelectBox={showSelectBox}
          selectedUserIds={selectedUserIds}
          selectableUserIdsForEmail={selectableUserIdsForEmail}
          allEmailRowsSelected={allEmailRowsSelected}
          someEmailRowsSelected={someEmailRowsSelected}
          canShowStaffActions={canShowStaffActions}
          canLogCall={canLogCall}
          canImpersonate={canImpersonate}
          isLibertyRole={isLibertyRole}
          canRemoveRole={canRemoveRole}
          canEdit={canEdit}
          onToggleSelectAllEmail={toggleSelectAllEmail}
          onToggleUserSelection={toggleUserSelection}
          onLogCall={setLogCallStaff}
          onImpersonate={handleImpersonate}
          onEditUser={handleEditUser}
          onRemoveRole={handleRemoveRoleClick}
          onWelcomeEmail={(staff) => setWelcomeConfirm({ isOpen: true, staff })}
        />
      </SectionCard>

      <PropertyContactsDialogs
        property={property}
        propertyId={propertyId}
        companyId={companyId}
        selectedUserIds={selectedUserIds}
        transferOpen={transferOpen}
        addUserOpen={addUserOpen}
        logCallStaff={logCallStaff}
        composeConfirmOpen={composeConfirmOpen}
        emailBroadcastOpen={emailBroadcastOpen}
        removeConfirm={removeConfirm}
        deleteUserConfirm={deleteUserConfirm}
        deleteOrDisableConfirm={deleteOrDisableConfirm}
        welcomeConfirm={welcomeConfirm}
        onTransferClose={() => setTransferOpen(false)}
        onTransferSuccess={onTransferSuccess}
        onAddUserClose={() => setAddUserOpen(false)}
        onAddUserSuccess={() => void loadStaff()}
        onLogCallClose={() => setLogCallStaff(null)}
        onComposeConfirmed={onComposeConfirmed}
        onComposeDismiss={() => setComposeConfirmOpen(false)}
        onEmailClose={() => setEmailBroadcastOpen(false)}
        onEmailSuccess={onEmailBroadcastDone}
        onRemoveRole={() => void executeRemoveRole()}
        onRemoveRoleDismiss={() => setRemoveConfirm({ isOpen: false, staff: null })}
        onDeleteUserChoice={handleDeleteUserChoice}
        onDeleteOrDisable={() => void executeDeleteOrDisable()}
        onDeleteOrDisableDismiss={() => setDeleteOrDisableConfirm({ isOpen: false, type: 0, userId: null })}
        onWelcomeEmail={() => void executeWelcomeEmail()}
        onWelcomeEmailDismiss={() => setWelcomeConfirm({ isOpen: false, staff: null })}
      />
    </div>
  )
}
